import { api } from "boot/axios";
import { i18n } from "boot/i18n";
import { normalizeApplicationProcessSteps } from "./stepMigration";

export async function getProjectIdeas(context) {
  try {
    const res = await api.get("/api/projects");
    context.commit("setProjectIdeas", res.data);
  } catch (error) {
    context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
  }
}

export async function getApplicationProcess(context, filters = {}) {
  try {
    // Build query string for filters
    let queryParams = '';

    if (Object.keys(filters).length > 0) {
      queryParams = '?';
      const params = [];

      // Add each filter to the params array if it exists
      if (filters.search) params.push(`search=${encodeURIComponent(filters.search)}`);
      if (filters.municipality) params.push(`municipality=${filters.municipality}`);
      if (filters.location) params.push(`location=${filters.location}`);
      if (filters.status) params.push(`status=${filters.status}`);
      if (filters.investive) params.push(`investive=${filters.investive}`);
      if (filters.categories) params.push(`categories=${filters.categories}`);
      if (filters.tags) params.push(`tags=${filters.tags}`);
      if (filters.applicationStep) params.push(`applicationStep=${filters.applicationStep}`);

      queryParams += params.join('&');
    }

    const res = await api.get(`/api/application/process${queryParams}`);
    context.commit("setApplicationProcess", res.data || []);
  } catch (error) {
    context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
  }
}

export async function getArchivedProjects(context, filters = {}) {
  try {
    let queryParams = '';

    if (Object.keys(filters).length > 0) {
      queryParams = '?';
      const params = [];

      if (filters.search) params.push(`search=${encodeURIComponent(filters.search)}`);
      if (filters.municipality) params.push(`municipality=${filters.municipality}`);
      if (filters.location) params.push(`location=${filters.location}`);
      if (filters.status) params.push(`status=${filters.status}`);
      if (filters.investive) params.push(`investive=${filters.investive}`);
      if (filters.categories) params.push(`categories=${filters.categories}`);
      if (filters.tags) params.push(`tags=${filters.tags}`);
      if (filters.applicationStep) params.push(`applicationStep=${filters.applicationStep}`);

      queryParams += params.join('&');
    }

    const res = await api.get(`/api/project/dashboard/archived${queryParams}`);
    context.commit("setArchivedProjects", res.data || []);
  } catch (error) {
    context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
  }
}

export async function getPrioritizedProjects(context, payload = {}) {
  try {
    const { municipalityId } = payload;
    const query = municipalityId ? `?municipality=${municipalityId}` : '';
    const res = await api.get(`/api/prioritized-projects${query}`);
    context.commit("setPrioritizedProjects", res.data || []);
  } catch (error) {
    context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
  }
}

export async function addToPriorityList(context, payload) {
  const { id } = payload;
  if (!!id) {
    try {
      await api.post("/api/prioritized-projects", { data: { project: id } });
      context.dispatch(
        "notifications/pushToast",
        { kind: "positive", title: i18n.t("Projektidee erfolgreich priorisiert") },
        { root: true }
      );
      context.dispatch("getPrioritizedProjects");
    } catch (error) {
      context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
      return false;
    }
  }
}

export async function removeFromPriorityList(context, payload) {
  const { id } = payload;
  if (!!id) {
    try {
      await api.delete(`/api/prioritized-projects/${id}`);
      context.dispatch(
        "notifications/pushToast",
        { kind: "positive", title: i18n.t("Projektidee erfolgreich aus der Priorisierung entfernt") },
        { root: true }
      );
      context.dispatch("getPrioritizedProjects");
    } catch (error) {
      context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
      return false;
    }
  }
}

export async function reorderPriorityList(context, payload) {
  const { order } = payload;
  try {
    await api.put("/api/prioritized-projects/reorder", { order });
    context.dispatch("getPrioritizedProjects");
  } catch (error) {
    context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
    context.dispatch("getPrioritizedProjects");
  }
}

export async function createNewProjectIdea(context, payload) {
  const { projectData } = payload;
  const { files, media, ...dataWithoutFiles } = projectData;

  if (!!projectData) {
    try {
      const res = await api.post("/api/projects?populate[0]=details", { data: dataWithoutFiles });

      if (projectData.files !== null && projectData.files.length > 0) {
        const fileUploadRes = await context.dispatch("uploadFiles", {
          files: projectData.files,
          id: res.data.data.id
        });
      }
      if (projectData.media !== null && projectData.media.length > 0) {
        const mediaUploadRes = await context.dispatch("uploadMedia", {
          media: projectData.media,
          id: res.data.data.id
        });
        console.log("mediaUploadRes", mediaUploadRes);
      }
      context.commit("addNewProjectIdea", res.data.data);
      context.commit("setCreatedProjectIdea", res.data.data);
      // Return the response data for the component to handle
      return res.data;
    } catch (error) {
      console.log("error.response", error.response);
      // Re-throw the error so the component can handle it
      throw error;
    }
  }
}

export async function uploadFiles(context, payload) {
  if (payload.files && payload.id) {
    let formData = new FormData();
    formData.append("ref", "api::project.project");
    formData.append("refId", payload.id);
    formData.append("field", "files");
    if (payload.files.length > 1) {
      payload.files.forEach(file => {
        formData.append("files", file);
      });
    } else {
      formData.append("files", payload.files[0]);
    }
    try {
      const fileRes = await api.post("api/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      console.log("fileRes", fileRes);
    } catch (error) {
      console.log("files error.response", error.response);
      context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
      // return false;
    }
  }
}

export async function uploadMedia(context, payload) {
  if (payload.media && payload.id) {
    if (payload.media.length > 0) {
      const promises = payload.media.map(async file => {
        const formData = new FormData();
        formData.append("ref", "api::project.project");
        formData.append("refId", payload.id);
        formData.append("field", "media");
        formData.append("files", file);
        formData.append(
          "fileInfo",
          JSON.stringify({
            caption: file.caption
          })
        );
        try {
          return await api.post("api/upload", formData, {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          });
        } catch (error) {
          console.log("media error.response", error.response);
          context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
        }
      });
      return Promise.all(promises);
    } else {
      console.error("No Meida Files");
    }
  }
}

export async function deleteFilesAndMedia(context, payload) {
  console.log("payload", payload);
  if (payload.data) {
    payload.data.forEach(async item => {
      try {
        const deleteRes = await api.delete(`api/upload/files/${item.id}`);
        console.log("deleteRes", deleteRes);
      } catch (error) {
        console.log("files error.response", error.response);
        context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
        // return false;
      }
    });
  }
}

export async function editProjectIdea(context, payload) {
  console.log("context.state.project", context.state.project);
  const { projectData: data} = payload;
  console.log("data :>> ", data);
  // delete property 'files' and 'media'
  const { files, media, ...dataWithoutFiles } = data;
  console.log("dataWithoutFiles", dataWithoutFiles);
  if (!!data) {
    try {
      const res = await api.put(`/api/projects/${data.id}?populate[0]=details`, {
        data: dataWithoutFiles
      });
      if (data.media !== null) {
        let added = [];
        if (context.state.project.media === null) {
          added = data.media;
        } else {
          added = data.media.filter(
            newMedia =>
              !context.state.project.media.find(
                oldMedia => oldMedia.id === newMedia.id
              )
          );
        }
        console.log("added", added);
        let deleted = [];
        if (context.state.project.media !== null) {
          deleted = context.state.project.media.filter(
            oldMedia =>
              !data.media.find(newMedia => newMedia.id === oldMedia.id)
          );
        }
        console.log("deleted", deleted);
        if (added.length > 0) {
          const addNewFilesRes = await context.dispatch("uploadMedia", {
            media: added,
            id: res.data.data.id
          });
          console.log("addNewFilesRes", addNewFilesRes);
        }
        if (deleted.length > 0) {
          const deleteFilesRes = await context.dispatch("deleteFilesAndMedia", {
            data: deleted
          });
          console.log("deleteFilesRes", deleteFilesRes);
        }
      }
      if (data.files !== null) {
        let added = [];
        if (context.state.project.files === null) {
          console.log("project files is null");
          added = data.files;
        } else {
          console.log("project files is not null");
          added = data.files.filter(
            newFiles =>
              !context.state.project.files.find(
                oldFiles => oldFiles.id === newFiles.id
              )
          );
        }
        let deleted = [];
        if (context.state.project.files !== null) {
          deleted = context.state.project.files.filter(
            oldFiles =>
              !data.files.find(newFiles => newFiles.id === oldFiles.id)
          );
        }
        if (added.length > 0) {
          const addNewFilesRes = await context.dispatch("uploadFiles", {
            files: added,
            id: res.data.data.id
          });
          console.log("addNewFilesRes", addNewFilesRes);
        }
        if (deleted.length > 0) {
          const deleteFilesRes = await context.dispatch("deleteFilesAndMedia", {
            data: deleted,
            id: res.data.data.id
          });
          console.log("deleteFilesRes", deleteFilesRes);
        }
      }
      context.dispatch(
        "notifications/pushToast",
        { kind: "positive", title: i18n.t("Projektidee erfolgreich bearbeitet") },
        { root: true }
      );
      context.commit("setCreatedProjectIdea", res.data.data);
      context.dispatch("getApplicationProcess");
      return res.data
    } catch (error) {
      console.error("error", error);
      context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
      return false;
    }
  }
}

export async function simpleUpdateProjectIdea(context, payload) {
  const { data } = payload;
  if (!!data) {
    try {
      const res = await api.put(`/api/projects/${data.id}`, {
        data: data
      });
      return res.data;
    }
    catch (error) {
      console.error("error", error);
      context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
      return false;
    }
  }
}

// vorpruefung-ticket's find/findOne/create/update actions are the untouched
// Strapi v4 core-controller defaults (only resend/findByToken/respondByToken
// are custom-overridden on the backend), so those responses still come back
// wrapped as { id, attributes: {...} } instead of flat like the rest of this
// app's custom-controller content-types. Flatten here to match what the rest
// of the codebase (and this component tree) expects.
function flattenVorpruefungTicket(entry) {
  if (!entry) return entry;
  const attrs = entry.attributes || entry;
  return { id: entry.id, ...attrs };
}

export async function fetchVorpruefungTickets(context, payload) {
  const { projectId } = payload;
  try {
    const res = await api.get(`/api/vorpruefung-tickets?filters[project]=${projectId}`);
    const raw = res.data.data || res.data || [];
    return raw.map(flattenVorpruefungTicket);
  } catch (error) {
    context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
    return [];
  }
}

export async function createVorpruefungTicket(context, payload) {
  const { projectId, type, notes } = payload;
  try {
    const res = await api.post("/api/vorpruefung-tickets", {
      data: { project: projectId, type, notes: notes || "" }
    });
    context.dispatch(
        "notifications/pushToast",
        { kind: "positive", title: i18n.t("Vorprüfung erfolgreich angefragt") },
        { root: true }
      );
    const entry = res.data.data || res.data;
    return flattenVorpruefungTicket(entry);
  } catch (error) {
    context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
    return false;
  }
}

export async function updateVorpruefungTicketNotes(context, payload) {
  const { id, notes } = payload;
  try {
    const res = await api.put(`/api/vorpruefung-tickets/${id}/notes`, {
      data: { notes }
    });
    const entry = res.data.data || res.data;
    return flattenVorpruefungTicket(entry);
  } catch (error) {
    context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
    return false;
  }
}

export async function resendVorpruefungTicket(context, payload) {
  const { id } = payload;
  try {
    await api.post(`/api/vorpruefung-tickets/${id}/resend`);
    context.dispatch(
        "notifications/pushToast",
        { kind: "positive", title: i18n.t("Vorprüfung erneut gesendet") },
        { root: true }
      );
    return true;
  } catch (error) {
    context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
    return false;
  }
}

// Records the decision for the project's current review of that type, whether
// it is still pending, already declined, or was never requested at all.
export async function overrideVorpruefungTicket(context, payload) {
  const { projectId, type, decisionType, responseText, wantsPhoneCall, wantsOnsiteMeeting, suggestedDates } = payload;
  try {
    await api.post("/api/vorpruefung-tickets/override", {
      project: projectId,
      type,
      decisionType,
      responseText,
      wantsPhoneCall,
      wantsOnsiteMeeting,
      suggestedDates
    });
    context.dispatch(
        "notifications/pushToast",
        { kind: "positive", title: i18n.t("projectComponents.aptitude.vorpruefung.overrideSuccess") },
        { root: true }
      );
    return true;
  } catch (error) {
    context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
    return false;
  }
}

// Silent on success: the caller (the funding-check step) already tells the user
// their funding selection changed, and a second toast would just be noise.
export async function resetVorpruefungTickets(context, payload) {
  const { projectId } = payload;
  try {
    await api.post("/api/vorpruefung-tickets/reset", { project: projectId });
    return true;
  } catch (error) {
    context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
    return false;
  }
}

// New action to update local state only
export function updateLocalProjectState(context, payload) {
  const { data } = payload;
  if (!!data) {
    try {
      // Update local Vuex state using mutations
      if (data.fundingCheckSteps) {
        context.commit("updateFundingCheckSteps", data.fundingCheckSteps);
      }
      if (data.projectDevelopmentSteps) {
        context.commit("updateProjectDevelopmentSteps", data.projectDevelopmentSteps);
      }
      if (data.projectApplicationSteps) {
        context.commit("updateProjectApplicationSteps", data.projectApplicationSteps);
      }
      if (data.applicationProcessSteps) {
        context.commit("updateApplicationProcessTabs", data.applicationProcessSteps);
      }
      return true;
    }
    catch (error) {
      console.error("Error updating local project state:", error);
      return false;
    }
  }
}

export async function getSpecificProject(context, payload) {
  const { id } = payload;
  // A refetch of the project already on screen keeps it until the fresh copy
  // arrives; blanking it would let a save in that window run against defaults.
  if (!context.state.project || context.state.project.id !== id) {
    context.commit("setSpecificProject", null);
  }
  if (id) {
    try {
      const res = await api.get(`/api/projects/${id}`);
      context.commit("setSpecificProject", normalizeApplicationProcessSteps(res.data));
    } catch (error) {
      console.log("error", error);
      context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
    }
  }
}

export async function tempCategories(context, payload) {
  context.commit("setTempCategories", payload);
}

export async function tempTags(context, payload) {
  context.commit("setTempTags", payload);
}

export async function addToWatchlist(context, payload) {
  const { id } = payload;
  if (!!id) {
    try {
      const res = await api.post("/api/watchlists", {
        data: { project: id }
      });
      console.log("res", res);
      context.dispatch(
        "notifications/pushToast",
        { kind: "positive", title: i18n.t("Projektidee erfolgreich zur Merkliste zugefügt") },
        { root: true }
      );
      context.dispatch("getProjectIdeas");
    } catch (error) {
      context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
      return false;
    }
  }
}

export async function removeFromWatchlist(context, payload) {
  const { id } = payload;
  if (!!id) {
    try {
      const res = await api.delete(`/api/watchlists/${id}`);
      console.log("res", res);
      context.dispatch(
        "notifications/pushToast",
        { kind: "positive", title: i18n.t("Projektidee erfolgreich von der Merkliste entfernt") },
        { root: true }
      );
      context.dispatch("getProjectIdeas");
    } catch (error) {
      context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
      return false;
    }
  }
}

export async function requestAccess(context, payload) {
  const { id } = payload;
  const { userId } = payload;
  const { type } = payload;
  const { guest } = payload;
  if (!!id && !!userId && !!type) {
    try {
      const res = await api.post("/api/requests", {
        data: {
          user: {
            id: userId
          },
          project: {
            id: id
          },
          type: type,
          guest: guest
        }
      });
      context.dispatch(
        "notifications/pushToast",
        { kind: "positive", title: i18n.t("Zugangsanfrage für Projektidee gesendet") },
        { root: true }
      );
      context.dispatch("getProjectIdeas");
    } catch (error) {
      context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
      return false;
    }
  }
}

export async function archiveProjectIdea(context, payload) {
  const { id } = payload;
  if (!!id) {
    try {
      const res = await api.put(`/api/projects/${id}`, {
        data: { archived: true }
      });
      console.log("res", res);
      context.dispatch(
        "notifications/pushToast",
        { kind: "positive", title: i18n.t("Projektidee erfolgreich archiviert") },
        { root: true }
      );
      context.commit("archiveProject");
      context.dispatch("getProjectIdeas");
      context.dispatch("getArchivedProjects");
    } catch (error) {
      context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
      return false;
    }
  }
}

export async function unarchiveProjectIdea(context, payload) {
  const { id } = payload;
  if (!!id) {
    try {
      await api.put(`/api/projects/${id}`, {
        data: { archived: false }
      });
      context.dispatch(
        "notifications/pushToast",
        { kind: "positive", title: i18n.t("Projektidee erfolgreich wiederhergestellt") },
        { root: true }
      );
      context.dispatch("getArchivedProjects");
    } catch (error) {
      context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
      return false;
    }
  }
}

export async function duplicateProject(context, payload) {
  const { id } = payload;
  if (!!id) {
    try {
      const res = await api.post(`/api/project/duplicate/${id}`);
      context.dispatch(
        "notifications/pushToast",
        { kind: "positive", title: i18n.t("Projektidee erfolgreich dupliziert") },
        { root: true }
      );
      context.dispatch("getProjectIdeas");
      return res;
    } catch (error) {
      context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
      return false;
    }
  }
}

export async function deleteProjectIdea(context, payload) {
  const { id } = payload;
  if (!!id) {
    try {
      const res = await api.delete(`/api/projects/${id}`);
      context.commit("deleteProjectIdea", res.data.data && res.data.data.id);
      context.dispatch(
        "notifications/pushToast",
        { kind: "positive", title: i18n.t("Projektidee erfolgreich gelöscht") },
        { root: true }
      );
      context.dispatch("getProjectIdeas");
    } catch (error) {
      context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
      return false;
    }
  }
}

export async function getProjectDashboardStats(context, filters = {}) {
  try {
    // Build query string for filters
    let queryParams = '';

    if (Object.keys(filters).length > 0) {
      queryParams = '?';
      const params = [];

      // Add each filter to the params array if it exists
      if (filters.search) params.push(`search=${encodeURIComponent(filters.search)}`);
      if (filters.municipality) params.push(`municipality=${filters.municipality}`);
      if (filters.location) params.push(`location=${filters.location}`);
      if (filters.status) params.push(`status=${filters.status}`);
      if (filters.investive) params.push(`investive=${filters.investive}`);
      if (filters.categories) params.push(`categories=${filters.categories}`);
      if (filters.tags) params.push(`tags=${filters.tags}`);
      if (filters.applicationStep) params.push(`applicationStep=${filters.applicationStep}`);

      queryParams += params.join('&');
    }

    const res = await api.get(`/api/project/dashboard/stat${queryParams}`);
    context.commit("setProjectDashboardStats", res.data);
  } catch (error) {
    context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: i18n.t(error.response.data.error.message) },
        { root: true }
      );
    return false;
  }
}

export async function validateApplicationAccess(context, id) {
  try {
    const res = await api.get(`/api/application/validate/${id}`);
    return res.data; // Returns { id, financialPlan, accessGranted }
  } catch (error) {
    console.error("Error validating application access:", error);
    context.dispatch(
        "notifications/pushToast",
        { kind: "negative", title: error.response?.data?.error?.message || "Error validating access" },
        { root: true }
      );
    return { accessGranted: false };
  }
}
